import { useQuery } from '@tanstack/react-query';
import type { inferRouterOutputs } from '@trpc/server';
import type { AppRouter } from '@servwell/api/router';
import { canHoldVisits, type Role, type WorkOrderConfirmInput } from '@servwell/shared';
import { useTRPC } from '../../lib/trpc';
import { Field, field } from '../create/Field';
import { Notice } from '../ui/Badge';
import { Button } from '../ui/Button';
import { StaleNotice, refreshFailed } from '../ui/QueryStates';

export type ConfirmationPlan =
  inferRouterOutputs<AppRouter>['workOrder']['confirmationPlan'];
export type ConfirmationResources = Pick<
  WorkOrderConfirmInput,
  'propertyId' | 'propertyResolutionReason' | 'resourceResolutions'
>;
type ResourceResolution = NonNullable<
  WorkOrderConfirmInput['resourceResolutions']
>[number];

/** Captures explicit execution decisions. Accepted economic fields are never editable here. */
export function WorkOrderConfirmationResources({
  plan,
  value,
  onChange,
  disabled = false,
}: {
  plan: ConfirmationPlan;
  value: ConfirmationResources;
  onChange: (next: ConfirmationResources) => void;
  disabled?: boolean;
}) {
  const trpc = useTRPC();
  const issues = plan.lines.filter((line) => line.issues.length > 0);
  const catalog = useQuery({
    ...trpc.priceBook.list.queryOptions(),
    enabled: issues.length > 0,
  });
  const locations = useQuery({
    ...trpc.inventory.locations.queryOptions(),
    enabled: issues.length > 0,
  });
  const members = useQuery({
    ...trpc.org.members.queryOptions(),
    enabled: issues.length > 0,
  });
  const customer = useQuery({
    ...trpc.customer.get.queryOptions({ customerId: plan.customerId }),
    enabled: plan.needsServiceAddress,
  });
  const failed =
    (issues.length > 0 && (catalog.isError || locations.isError || members.isError)) ||
    (plan.needsServiceAddress && customer.isError);
  const patch = (quoteLineId: string, changes: Partial<ResourceResolution>) => {
    const current = value.resourceResolutions ?? [];
    const previous = current.find((row) => row.quoteLineId === quoteLineId) ?? {
      quoteLineId,
      reason: '',
    };
    const next = { ...previous, ...changes };
    onChange({
      ...value,
      resourceResolutions: [
        ...current.filter((row) => row.quoteLineId !== quoteLineId),
        next,
      ],
    });
  };
  return (
    <div className="space-y-3">
      <p className="text-xs text-muted-foreground">
        Accepted quantities, prices, tax and descriptions stay unchanged. These decisions
        apply to work order execution and are checked again when confirmed.
      </p>
      <StaleNotice
        show={
          refreshFailed(catalog, catalog.data) ||
          refreshFailed(locations, locations.data) ||
          refreshFailed(members, members.data) ||
          refreshFailed(customer, customer.data) ||
          catalog.isPaused ||
          locations.isPaused ||
          members.isPaused ||
          customer.isPaused
        }
        onRetry={() => {
          void catalog.refetch();
          void locations.refetch();
          void members.refetch();
          if (plan.needsServiceAddress) void customer.refetch();
        }}
      />
      {failed ? (
        <Notice tone="warn">
          Resource choices could not load. Your decisions remain in this form.
          <Button
            size="sm"
            variant="ghost"
            onClick={() => {
              void catalog.refetch();
              void locations.refetch();
              void members.refetch();
              if (plan.needsServiceAddress) void customer.refetch();
            }}
          >
            Refresh resource choices
          </Button>
        </Notice>
      ) : null}
      {plan.needsServiceAddress ? (
        <fieldset
          disabled={disabled}
          className="grid gap-3 rounded-[var(--radius)] border border-border p-3 sm:grid-cols-2"
        >
          <legend className="px-1 text-xs font-semibold">
            Historical quote missing a service address
          </legend>
          <Field label="Service address for this work order">
            <select
              className={field}
              value={value.propertyId ?? ''}
              disabled={
                disabled || customer.isPending || customer.isError || customer.isFetching
              }
              onChange={(event) =>
                onChange({ ...value, propertyId: event.target.value || undefined })
              }
            >
              <option value="">Choose the customer's address</option>
              {(customer.data?.properties ?? []).map((property) => (
                <option key={property.id} value={property.id}>
                  {property.label}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Address decision reason">
            <input
              className={field}
              maxLength={500}
              value={value.propertyResolutionReason ?? ''}
              onChange={(event) =>
                onChange({ ...value, propertyResolutionReason: event.target.value })
              }
            />
          </Field>
        </fieldset>
      ) : null}
      {issues.map((line) => {
        const resolution = value.resourceResolutions?.find(
          (row) => row.quoteLineId === line.quoteLineId,
        );
        const hasItemIssue = line.issues.some((issue) => issue.code.startsWith('item_'));
        return (
          <fieldset
            key={line.quoteLineId}
            disabled={disabled}
            className="space-y-3 rounded-[var(--radius)] border border-border p-3"
          >
            <legend className="px-1 text-sm font-semibold">{line.name}</legend>
            <p className="text-xs text-muted-foreground">
              {line.taskName ?? 'General work'} · Accepted item:{' '}
              {line.itemName ?? 'Custom line'} · Source:{' '}
              {line.sourceLocationName ?? 'Not selected'} · Assignee:{' '}
              {line.assignedUserName ?? 'Not available or unassigned'}
            </p>
            <ul className="list-disc space-y-1 pl-4 text-xs">
              {line.issues.map((issue) => (
                <li key={issue.code}>{issue.message}</li>
              ))}
            </ul>
            <div className="grid gap-3 sm:grid-cols-2">
              {hasItemIssue ? (
                <Field label="Current fulfillment item">
                  <select
                    className={field}
                    value={resolution?.priceBookItemId ?? ''}
                    disabled={
                      disabled ||
                      catalog.isPending ||
                      catalog.isError ||
                      catalog.isFetching
                    }
                    onChange={(event) =>
                      patch(line.quoteLineId, {
                        priceBookItemId: event.target.value || undefined,
                        acknowledgeRetiredItem: false,
                      })
                    }
                  >
                    <option value="">Keep the accepted item</option>
                    {(catalog.data ?? []).map((item) => (
                      <option key={item.id} value={item.id}>
                        {item.name} · {item.kind}
                      </option>
                    ))}
                  </select>
                </Field>
              ) : null}
              {line.kind === 'material' ? (
                <Field label="Active stock location">
                  <select
                    className={field}
                    value={resolution?.sourceLocationId ?? ''}
                    disabled={
                      disabled ||
                      locations.isPending ||
                      locations.isError ||
                      locations.isFetching
                    }
                    onChange={(event) =>
                      patch(line.quoteLineId, {
                        sourceLocationId: event.target.value || undefined,
                      })
                    }
                  >
                    <option value="">Keep the accepted source</option>
                    {(locations.data ?? []).map((location) => (
                      <option key={location.id} value={location.id}>
                        {location.name}
                      </option>
                    ))}
                  </select>
                </Field>
              ) : null}
              {line.issues.some((issue) => issue.code === 'assignee_unavailable') ? (
                <Field label="Responsible crew member">
                  <select
                    className={field}
                    value={
                      resolution?.assignedUserId === null
                        ? 'unassigned'
                        : (resolution?.assignedUserId ?? '')
                    }
                    disabled={
                      disabled ||
                      members.isPending ||
                      members.isError ||
                      members.isFetching
                    }
                    onChange={(event) =>
                      patch(line.quoteLineId, {
                        assignedUserId:
                          event.target.value === 'unassigned'
                            ? null
                            : event.target.value || undefined,
                      })
                    }
                  >
                    <option value="">Keep the accepted assignment</option>
                    <option value="unassigned">Explicitly leave unassigned</option>
                    {(members.data ?? [])
                      .filter((person) => canHoldVisits(person.role as Role))
                      .map((person) => (
                        <option key={person.userId} value={person.userId}>
                          {person.name ?? person.email}
                        </option>
                      ))}
                  </select>
                </Field>
              ) : null}
              <Field label="Resource decision reason">
                <input
                  className={field}
                  maxLength={500}
                  value={resolution?.reason ?? ''}
                  onChange={(event) =>
                    patch(line.quoteLineId, { reason: event.target.value })
                  }
                />
              </Field>
            </div>
            {line.issues.some((issue) => issue.code === 'item_retired') ? (
              <label className="flex items-start gap-2 text-sm">
                <input
                  type="checkbox"
                  className="mt-1"
                  checked={resolution?.acknowledgeRetiredItem ?? false}
                  disabled={disabled || Boolean(resolution?.priceBookItemId)}
                  onChange={(event) =>
                    patch(line.quoteLineId, {
                      acknowledgeRetiredItem: event.target.checked,
                    })
                  }
                />
                <span>Use the accepted retired item</span>
              </label>
            ) : null}
          </fieldset>
        );
      })}
      {!issues.length && !plan.needsServiceAddress ? (
        <p className="text-xs text-muted-foreground">
          No resource decisions are currently required.
        </p>
      ) : null}
    </div>
  );
}
